'use client';

import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { cn, type WithUndefined } from '@/lib/utils';

/**
 * Checkbox Variants Configuration
 *
 * Defines visual styles for the Checkbox component using design tokens.
 * States: checked, unchecked, indeterminate
 */
const checkboxStyles = {
  base: 'peer h-4 w-4 shrink-0 rounded-sm border border-border-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50',
  checked:
    'bg-primary-500 border-primary-500 text-white hover:bg-primary-600 hover:border-primary-600',
  unchecked:
    'bg-white border-border-primary hover:border-border-secondary dark:bg-neutral-900 dark:border-border-primary dark:hover:border-border-secondary',
  indeterminate:
    'bg-primary-500 border-primary-500 text-white hover:bg-primary-600 hover:border-primary-600',
  dark: {
    checked: 'dark:bg-primary-500 dark:border-primary-500',
    unchecked:
      'dark:bg-neutral-900 dark:border-border-primary dark:hover:border-border-secondary',
    indeterminate: 'dark:bg-primary-500 dark:border-primary-500',
  },
};

export type CheckboxProps = WithUndefined<React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>> & {
  /** If true, the checkbox is in an indeterminate state */
  indeterminate?: boolean | undefined;
  /** Additional CSS classes */
  className?: string | undefined;
  /** Label text to display next to the checkbox */
  label?: string | undefined;
  /** Helper text to display below the checkbox */
  helperText?: string | undefined;
  /** If true, displays a required indicator */
  required?: boolean | undefined;
  /** Error message to display below the checkbox */
  error?: string | undefined;
};

/**
 * Checkbox Component
 *
 * A fully accessible checkbox component using Radix UI primitives.
 * Supports checked, unchecked, and indeterminate states.
 * Integrates with design tokens for light/dark mode support.
 *
 * @example
 * ```tsx
 * // Basic checkbox
 * <Checkbox />
 *
 * // Checked checkbox
 * <Checkbox checked={true} />
 *
 * // With label
 * <Checkbox label="I agree to the terms" />
 *
 * // Indeterminate state (partial selection)
 * <Checkbox indeterminate />
 *
 * // With helper text
 * <Checkbox
 *   label="Subscribe to updates"
 *   helperText="Receive weekly notifications"
 * />
 *
 * // With error state
 * <Checkbox
 *   label="Accept terms"
 *   error="You must accept the terms to continue"
 * />
 *
 * // Disabled state
 * <Checkbox label="This option is disabled" disabled />
 *
 * // With react-hook-form
 * <Checkbox label="Remember me" {...register('rememberMe')} />
 * ```
 */
const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(
  (
    {
      checked,
      indeterminate = false,
      className,
      label,
      helperText,
      required,
      error,
      disabled,
      onCheckedChange,
      ...props
    },
    ref
  ) => {
    // Determine visual state
    const isIndeterminate = indeterminate || checked === 'indeterminate';

    return (
      <div className="w-full">
        <div className="flex items-center gap-2">
          <CheckboxPrimitive.Root
            ref={ref}
            checked={isIndeterminate ? 'indeterminate' : (checked ?? false)}
            {...(onCheckedChange !== undefined ? { onCheckedChange } : {})}
            disabled={disabled ?? false}
            className={cn(
              checkboxStyles.base,
              isIndeterminate
                ? checkboxStyles.indeterminate
                : checked === true
                  ? checkboxStyles.checked
                  : checkboxStyles.unchecked,
              checkboxStyles.dark[isIndeterminate ? 'indeterminate' : checked === true ? 'checked' : 'unchecked'],
              className
            )}
            {...(props as React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>)}
          >
            <CheckboxPrimitive.Indicator className="flex items-center justify-center">
              {isIndeterminate ? (
                <Minus className="h-3 w-3" />
              ) : (
                <Check className="h-3 w-3" />
              )}
            </CheckboxPrimitive.Indicator>
          </CheckboxPrimitive.Root>

          {/* Label */}
          {label && (
            <label
              className="text-sm font-medium text-text-primary cursor-pointer hover:text-text-secondary transition-colors"
              onClick={(e) => {
                // This allows clicking the label to toggle the checkbox
                e.preventDefault();
              }}
            >
              {label}
              {required && <span className="ml-1 text-error-500">*</span>}
            </label>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <p className="mt-1 text-sm text-error-500">
            {error}
          </p>
        )}

        {/* Helper Text */}
        {helperText && !error && (
          <p className="mt-1 text-sm text-text-secondary">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export { Checkbox };
